import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import { createCoverageEvent, listSubjectTypes, searchSubjects } from './api'
import { CLASSIFICATION_ORDER, EVENT_TYPE_ORDER, classificationLabel, eventTypeLabel } from './vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse, CoverageSubjectResponse, CoverageSubjectTypeOption } from '@/shared/api/types'
import { DateTimePicker } from '@/components/erp/date-picker'

/** `datetime-local` refuses a zoned ISO string, so the value is local-shifted. */
function nowForInput(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

export function CoverageEventDialog({
  onClose, onCreated,
}: { onClose: () => void; onCreated: (e: CoverageEventResponse) => void }) {
  const [title, setTitle] = useState('')
  const [eventTypeCode, setEventTypeCode] = useState<string>('RALLY')
  const [classificationCode, setClassificationCode] = useState('INTERNAL')
  const [scheduledStart, setScheduledStart] = useState(nowForInput())
  const [scheduledEnd, setScheduledEnd] = useState('')
  const [unit, setUnit] = useState<CascadeValue>({ path: [] })
  const [venue, setVenue] = useState('')
  const [expected, setExpected] = useState('')
  const [description, setDescription] = useState('')
  const [openMainCamera, setOpenMainCamera] = useState(true)
  const [subjectType, setSubjectType] = useState('')
  const [subject, setSubject] = useState<CoverageSubjectResponse | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const unitId = unit.unitId ?? null

  return (
    <Modal open onClose={onClose} title="New coverage event" width="lg">
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Title" required>
          <input
            className="erp-input w-full"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Kamukunji rally — final push"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            <select
              className="erp-input w-full"
              value={eventTypeCode}
              onChange={(e) => setEventTypeCode(e.target.value)}
            >
              {EVENT_TYPE_ORDER.map((t) => (
                <option key={t} value={t}>{eventTypeLabel(t)}</option>
              ))}
            </select>
          </Field>

          {/* The order here is the RANK order, not alphabetical — it is a ladder
              and reading it as one is the point. */}
          <Field label="Who may see it">
            <select
              className="erp-input w-full"
              value={classificationCode}
              onChange={(e) => setClassificationCode(e.target.value)}
            >
              {CLASSIFICATION_ORDER.map((c) => (
                <option key={c} value={c}>{classificationLabel(c)}</option>
              ))}
            </select>
          </Field>
        </div>

        <Note tone="info">
          Anyone whose clearance reaches this level can see the event and watch its cameras.
          You cannot classify an event above your own clearance — the server refuses it, so
          that you never create something you then cannot open.
        </Note>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts" required>
            <DateTimePicker value={scheduledStart} onChange={setScheduledStart} className="w-full" />
          </Field>
          <Field label="Ends">
            <DateTimePicker value={scheduledEnd} onChange={setScheduledEnd} className="w-full" />
          </Field>
        </div>

        <Field label="Where">
          <UnitCascade value={unit} onChange={setUnit} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Venue">
            <input
              className="erp-input w-full"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="Kamukunji Grounds"
            />
          </Field>
          <Field label="Expected attendance">
            <input
              type="number"
              min={0}
              className="erp-input w-full"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              placeholder="Optional"
            />
          </Field>
        </div>

        <SubjectPicker
          typeCode={subjectType}
          onTypeChange={(c) => { setSubjectType(c); setSubject(null) }}
          selected={subject}
          onSelect={setSubject}
        />

        <Field label="Notes">
          <textarea
            className="erp-input min-h-[64px] w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — running order, who is speaking, what to watch for."
          />
        </Field>

        <label className="flex items-center gap-2 text-erp-sm">
          <input
            type="checkbox"
            checked={openMainCamera}
            onChange={(e) => setOpenMainCamera(e.target.checked)}
          />
          Also open a “Main camera” slot
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={saving || !title.trim()}
            onClick={async () => {
              setSaving(true); setError(null)
              try {
                const created = await createCoverageEvent({
                  title: title.trim(),
                  description: description.trim() || null,
                  eventTypeCode,
                  classificationCode,
                  unitId,
                  venue: venue.trim() || null,
                  scheduledStart: new Date(scheduledStart).toISOString(),
                  scheduledEnd: scheduledEnd ? new Date(scheduledEnd).toISOString() : null,
                  expectedAttendance: expected ? Number(expected) : null,
                  subjectTypeCode: subject ? subject.subjectTypeCode : null,
                  subjectRef: subject ? subject.ref : null,
                  // The camera slot is created in the SAME transaction server-side.
                  // PollSphere posts it as a second request and swallows the failure,
                  // so its dialog reports success on an event with no camera.
                  openMainCamera,
                })
                onCreated(created)
              } catch (e) { setError(explain(e)) } finally { setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create event
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * What this event is coverage OF.
 *
 * The type list comes from the server, which builds it from the resolvers on the
 * classpath. A hardcoded list would be wrong on the day `survey` ships and wrong
 * on every deployment that has a module this one does not.
 */
export function SubjectPicker({
  typeCode, onTypeChange, selected, onSelect,
}: {
  typeCode: string
  onTypeChange: (code: string) => void
  selected: CoverageSubjectResponse | null
  onSelect: (s: CoverageSubjectResponse | null) => void
}) {
  const [types, setTypes] = useState<CoverageSubjectTypeOption[] | null>(null)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<CoverageSubjectResponse[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    let cancelled = false
    listSubjectTypes()
      .then((t) => { if (!cancelled) setTypes(t) })
      // No resolvers, or the call failed: the picker simply does not appear. An
      // attachment is optional everywhere, so a missing one is not an error state.
      .catch(() => { if (!cancelled) setTypes([]) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!typeCode) { setHits([]); return }
    let cancelled = false
    setSearching(true)
    const t = setTimeout(() => {
      searchSubjects(typeCode, q)
        .then((r) => { if (!cancelled) setHits(r) })
        .catch(() => { if (!cancelled) setHits([]) })
        .finally(() => { if (!cancelled) setSearching(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [typeCode, q])

  if (types === null || types.length === 0) return null

  return (
    <Field label="Coverage of">
      <div className="space-y-2">
        <select
          className="erp-input w-full"
          value={typeCode}
          onChange={(e) => onTypeChange(e.target.value)}
          aria-label="What this covers"
        >
          <option value="">Nothing in particular</option>
          {types.map((t) => (
            <option key={t.subjectTypeCode} value={t.subjectTypeCode}>{t.label}</option>
          ))}
        </select>

        {typeCode && !selected && (
          <>
            <input
              className="erp-input w-full"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              aria-label="Search records to attach"
            />
            {searching ? (
              <p className="text-erp-xs text-muted-foreground">Searching…</p>
            ) : hits.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                {q ? 'Nothing matched.' : 'Type to search.'}
              </p>
            ) : (
              <ul className="max-h-40 overflow-y-auto rounded-md border border-border">
                {hits.map((h) => (
                  <li key={h.ref ?? h.label ?? ''}>
                    <button
                      type="button"
                      className="w-full px-2 py-1.5 text-left text-erp-sm hover:bg-muted"
                      onClick={() => onSelect(h)}
                    >
                      <span className="font-medium">{h.label}</span>
                      {h.secondaryLabel && (
                        <span className="ml-1.5 text-erp-xs text-muted-foreground">{h.secondaryLabel}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {selected && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5">
            <span className="truncate text-erp-sm font-medium">{selected.label}</span>
            {selected.secondaryLabel && (
              <span className="truncate text-erp-xs text-muted-foreground">{selected.secondaryLabel}</span>
            )}
            <button
              type="button"
              className="erp-btn-ghost ml-auto h-6 shrink-0 text-erp-xs text-chocolate"
              onClick={() => onSelect(null)}
            >
              Change
            </button>
          </div>
        )}
      </div>
    </Field>
  )
}
